import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { actionBarRecipe } from './recipes/action-bar.recipe';
import { alertRecipe } from './recipes/alert.recipe';
import { badgeRecipe } from './recipes/badge.recipe';
import { barSegmentRecipe } from './recipes/bar-segment.recipe';
import { buttonRecipe } from './recipes/button.recipe';
import { cardRecipe } from './recipes/card';
import { checkboxRecipe } from './recipes/checkbox.recipe';
import { comboboxRecipe } from './recipes/combobox';
import { datePickerRecipe } from './recipes/date-picker.recipe';
import { fieldRecipe } from './recipes/field.recipe';
import { headingRecipe } from './recipes/heading.recipe';
import { inputRecipe } from './recipes/input.recipe';
import { nativeSelectRecipe } from './recipes/native-select';
import { numberInputRecipe } from './recipes/number-input.recipe';
import { popoverRecipe } from './recipes/popover.recipe';
import { radioCardRecipe } from './recipes/radio-card.recipe';
import { selectRecipe } from './recipes/select.recipe';
import { switchRecipe } from './recipes/switch.recipe';
import { dialogRecipe, drawerRecipe } from './recipes/overlay.recipe';
import { tableRecipe } from './recipes/table.recipe';
import { tabsRecipe } from './recipes/tabs.recipe';
import { menuRecipe } from './recipes/menu.recipe';
import { tagsInputRecipe } from './recipes/tags-input.recipe';
import { textareaRecipe } from './recipes/textarea.recipe';
import { toastRecipe } from './recipes/toast.recipe';
import { tooltipRecipe } from './recipes/tooltip.recipe';
// Create a custom system with Figtree (headings/display) and Geist (body) fonts
export const config = defineConfig({
    globalCss: {
        body: {
            bg: '{colors.background}',
            color: '{colors.dark}',
            fontSize: '16px',
        },
        // iOS Safari zooms into any field whose text is under 16px when it takes
        // focus, and keeps the zoom after the keyboard closes: sticky banners end up
        // cut off at the edge. `-webkit-touch-callout` only exists on iOS, so other
        // browsers keep the recipes' sizes. `!important` because recipes sit in a
        // later cascade layer than global CSS.
        'input, textarea, select': {
            '@supports (-webkit-touch-callout: none)': {
                fontSize: '16px !important',
            },
        },
    },
    theme: {
        tokens: {
            radii: {
                xs: { value: '4px' },
                sm: { value: '6px' },
                md: { value: '8px' },
                lg: { value: '12px' },
                xl: { value: '16px' },
                '2xl': { value: '20px' },
                '3xl': { value: '28px' },
                // Role token, not a geometric alias of `full`. Reserved for the
                // full-width CTAs on the auth surface (login, invitation, magic-link),
                // which are single-choice front-door controls rather than in-app
                // chrome, and sanctioned on the creator space too (filter chips, small
                // row buttons). Keeping it named means the radius can be retuned in one
                // place; dashboard buttons keep the `md` default so that product
                // surface never drifts into pill chrome.
                pill: { value: '9999px' },
                // Creator space geometry (additive, read only by the portal): the
                // primary call to action and the fields, notices and choices.
                cta: { value: '18px' },
                control: { value: '14px' },
            },
            zIndex: {
                dropdown: { value: 2000 },
            },
            fonts: {
                heading: { value: "'Figtree Variable', sans-serif" },
                body: { value: "'Geist', sans-serif" },
                // Geist Mono is loaded by the dashboard (Google Fonts); the system
                // stack covers surfaces that don't load it. Bare `monospace` resolves
                // to Courier and reads serif-ish, defeating the point of a code chip.
                mono: { value: "'Geist Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace" },
                display: { value: "'Figtree Variable', sans-serif" },
            },
            fontSizes: {
                xs: { value: '12px' },
                sm: { value: '14px' },
                md: { value: '16px' },
                lg: { value: '18px' },
                xl: { value: '20px' },
                '2xl': { value: '24px' },
                '3xl': { value: '32px' },
                '4xl': { value: '40px' },
                '5xl': { value: '48px' },
            },
            shadows: {
                // Rebrand: no shadows on surfaces (borders + bg contrast only). Neutralized
                // at the token so every hardcoded shadow="card" / "cardHover" usage across
                // the app flattens at once, without touching each call site.
                card: { value: 'none' },
                cardHover: { value: 'none' },
                // Deliberate exceptions to the no-shadow rule above. The product
                // announcement panel is a floating overlay, not a page surface: it
                // needs to detach from whatever it covers, and its status dot needs a
                // halo ring rather than a drop shadow. Scoped to `announcement.*` so
                // they cannot leak into card chrome.
                announcement: {
                    panel: { value: '0 18px 48px rgba(23, 23, 21, 0.30)' },
                    dot: { value: '0 0 0 3px rgba(65, 211, 141, 0.22)' },
                },
                // Same kind of exception: the page-change loader is a line of light,
                // not a surface, and its neon halo is what makes it read at a glance.
                // Scoped to `loader.*`, never for cards or controls.
                loader: {
                    neon: {
                        value: '0 0 4px 0 rgba(185, 167, 255, 0.95), 0 0 12px 2px rgba(130, 104, 192, 0.6)',
                    },
                    spark: { value: '0 0 8px 2px rgba(255, 188, 242, 0.95)' },
                },
            },
            colors: {
                // Single warm cream shared by the page bg, sidebar, and the opt-in
                // content canvas (white cards/tab strips float on top of it).
                background: { value: '#FBF9F6' },
                panelBg: { value: '#FFFFFF' },
                cardBg: { value: '{colors.panelBg}' },
                sidebarBg: { value: '{colors.background}' },
                cardBorder: { value: '#EBE9E1' },
                // Warm charcoal for inactive tab labels and secondary chrome on the
                // cream and white surfaces. Comfortably AA on cream.
                navInactive: { value: '#45423C' },
                // Light navigation chrome still used on the cream surfaces (studio
                // rails, contact card, companion). The dashboard menu itself is dark
                // and reads `navDark.*` below.
                navHoverBg: { value: 'rgba(26, 23, 20, 0.05)' },
                navSubInactive: { value: '#8A8178' },
                navSubBorder: { value: '#E3DED1' },
                navFooterBorder: { value: '#EFECE3' },
                deepForest: { value: '#001612' },
                deepForestHover: { value: '#002f26' },
                softCream: { value: '#F4F3EC' },
                babyPowder: { value: '#FAF9F5' },
                whiteSmoke: { value: '#F4F3F2' },
                seaSalt: { value: '#F6F6F7' },
                dark: { value: '#000000' },
                darkNav: { value: '#1a1a1a' },
                // Dark navigation frame: the top bar and the left menu read as one
                // charcoal L around the white content, internal panels (Settings,
                // Inbox, Program) included. Every alpha is expressed against `darkNav`
                // — do not reuse these on the cream or white surfaces, where the
                // light `nav*` tokens above still apply.
                navDark: {
                    text: { value: 'rgba(255, 255, 255, 0.88)' },
                    textStrong: { value: '#FFFFFF' },
                    textMuted: { value: 'rgba(255, 255, 255, 0.6)' },
                    textSubtle: { value: 'rgba(255, 255, 255, 0.45)' },
                    subText: { value: 'rgba(255, 255, 255, 0.7)' },
                    sectionLabel: { value: 'rgba(255, 255, 255, 0.36)' },
                    chevron: { value: 'rgba(255, 255, 255, 0.4)' },
                    hoverBg: { value: 'rgba(255, 255, 255, 0.07)' },
                    subHoverBg: { value: 'rgba(255, 255, 255, 0.05)' },
                    activeBg: { value: 'rgba(255, 255, 255, 0.1)' },
                    activeBorder: { value: 'rgba(255, 255, 255, 0.08)' },
                    subBorder: { value: 'rgba(255, 255, 255, 0.1)' },
                    footerBorder: { value: 'rgba(255, 255, 255, 0.08)' },
                    badgeBg: { value: 'rgba(255, 255, 255, 0.1)' },
                    badgeText: { value: 'rgba(255, 255, 255, 0.82)' },
                    // Raised block on the dark menu: workspace switcher, companion card,
                    // search field.
                    surface: { value: 'rgba(255, 255, 255, 0.05)' },
                    surfaceBorder: { value: 'rgba(255, 255, 255, 0.08)' },
                    surfaceHoverBg: { value: 'rgba(255, 255, 255, 0.08)' },
                    surfaceHoverBorder: { value: 'rgba(255, 255, 255, 0.12)' },
                    focusBorder: { value: 'rgba(255, 255, 255, 0.36)' },
                    skeleton: { value: 'rgba(255, 255, 255, 0.08)' },
                    avatarRing: { value: 'rgba(255, 255, 255, 0.14)' },
                    newTagBg: { value: 'rgba(125, 211, 252, 0.14)' },
                    newTagText: { value: '#7DD3FC' },
                    liveBg: { value: 'rgba(65, 211, 141, 0.14)' },
                    liveText: { value: '#41D38D' },
                    aiBg: { value: 'rgba(185, 167, 255, 0.14)' },
                    aiText: { value: '#B9A7FF' },
                    dangerText: { value: '#FCA5A5' },
                    // Lilac bloom anchored on the top-left corner of the top bar and
                    // continued, offset, down the menu (see `navVeilBackground`).
                    veil: { value: 'rgba(185, 167, 255, 0.13)' },
                    veilFade: { value: 'rgba(185, 167, 255, 0.04)' },
                },
                // Warm-neutral ink ramp for the cream surfaces. Distinct from
                // `dark`/`gray.*`, which are neutral — these carry the same warmth as
                // the cream backgrounds they sit on, and from `navInactive`, which is
                // reserved for sidebar/tab chrome.
                // Consumed by the notifications panel today; auth and onboarding still
                // hard-code these exact hexes and are yet to be migrated onto them.
                ink: { value: '#171715' },
                inkMuted: { value: '#777166' },
                inkSubtle: { value: '#A29B8D' },
                // Warm hairlines of the dashboard's controls and tables, one family
                // from the strongest to the faintest: an outline control (filter
                // trigger, pagination, row pill), its hover, a field or icon well, and
                // the separator between table rows.
                hairline: {
                    control: { value: '#D6D1C4' },
                    controlHover: { value: '#C9C4B8' },
                    field: { value: '#E3DFD5' },
                    row: { value: '#F1EFE9' },
                },
                // Hovered table row, and the placeholder blocks of a loading table.
                rowHoverBg: { value: '#FAF8F3' },
                skeletonBg: { value: '#EDEAE4' },
                activeSidebarLink: {
                    // Flat white tile with a warm hairline (no blur, no shadow): the
                    // selected item of a control sitting on a cream track.
                    bg: { value: '#FFFFFF' },
                    border: { value: '#E6E2D6' },
                },
                'light-gray-1': { value: 'colors.gray.50' },
                'light-gray-2': { value: 'colors.gray.100' },
                gray: {
                    50: { value: '#f6f6f6' },
                    100: { value: '#eeeeee' },
                    200: { value: '#d1d1d1' },
                    300: { value: '#b0b0b0' },
                    400: { value: '#888888' },
                    500: { value: '#686868' },
                    600: { value: '#5d5d5d' },
                    700: { value: '#4f4f4f' },
                    800: { value: '#454545' },
                    900: { value: '#1D1E20' },
                    950: { value: '#0F0F10' },
                },
                primary: {
                    // Rebrand: warm neutral charcoal ramp. Replaces the legacy indigo so
                    // BOTH colorPalette="primary" (semantic) and primary.NNN (scale) resolve
                    // to neutral ink, no indigo leaks anywhere.
                    50: { value: '#F4F4F2' },
                    100: { value: '#E9E8E4' },
                    200: { value: '#D5D3CC' },
                    300: { value: '#B6B3AA' },
                    400: { value: '#8F8B81' },
                    500: { value: '#67635A' },
                    600: { value: '#47433C' },
                    700: { value: '#322F2A' },
                    800: { value: '#23211D' },
                    900: { value: '#1A1A1A' },
                    950: { value: '#0F0F0E' },
                },
                green: {
                    50: { value: '#eefbf6' },
                    100: { value: '#d6f5e7' },
                    200: { value: '#aee9d1' },
                    300: { value: '#7dd8b9' },
                    400: { value: '#48bf9a' },
                    500: { value: '#25a481' },
                    600: { value: '#178468' },
                    700: { value: '#126a56' },
                    800: { value: '#115445' },
                    900: { value: '#0f453a' },
                    950: { value: '#072721' },
                },
                lilac: {
                    50: { value: '#F7F3FC' },
                    100: { value: '#F3EDFB' },
                    200: { value: '#E9E0F7' },
                    300: { value: '#D9C9F0' },
                    400: { value: '#B49BDE' },
                    500: { value: '#9678CC' },
                    600: { value: '#8268C0' },
                    700: { value: '#6C4FA3' },
                    800: { value: '#573F83' },
                    900: { value: '#473568' },
                    950: { value: '#2C2040' },
                },
                // Rose highlight accent. Distinct from lilac (which stays the AI
                // signature for badges and gradients): rose is reserved for spotlighting
                // a few ultra-important elements (e.g. spark icons). Anchored at 300 on
                // the reference pink (#FFBCF2); darker steps give contrast on the cream
                // surfaces, the light steps read on dark surfaces.
                rose: {
                    50: { value: '#FFF5FD' },
                    100: { value: '#FFEAFB' },
                    200: { value: '#FFD3F6' },
                    300: { value: '#FFBCF2' },
                    400: { value: '#F58EE2' },
                    500: { value: '#E866CE' },
                    600: { value: '#D14AB5' },
                    700: { value: '#A8388F' },
                    800: { value: '#852D71' },
                    900: { value: '#6B255B' },
                    950: { value: '#3F1536' },
                },
                // "Per-recipient" blue family. Collab brief templates split every field
                // into shared narrative (charcoal/cream, the app default) and
                // per-recipient values (compensation, deliverable quantities, timeline)
                // that each proposal can override. The blue tokens mark the per-recipient
                // fields consistently — dashed borders, tinted fills, blue text — and have
                // no equivalent in the neutral rebrand ramp, so they live here as their
                // own semantic family (never call them "blue" at the call site).
                perRecipient: {
                    // Dashed border on per-recipient section cards and variable controls.
                    border: { value: '#7FA6E8' },
                    // Softer hairline used on the "Per creator" title badge.
                    borderSoft: { value: '#C9DBF8' },
                    // Tinted fills: `surface` for control backgrounds, `surfaceSubtle` for
                    // the lighter badge/tile background.
                    surface: { value: '#F6F9FE' },
                    surfaceSubtle: { value: '#EAF1FD' },
                    // Primary value text (amounts, quantities, day counts).
                    text: { value: '#2456B0' },
                    // Muted secondary text and +/- stepper glyphs.
                    textMuted: { value: '#6D8BBD' },
                },
                // Amber warning used only for the "Brand DNA not extracted yet" note on
                // the AI generation card. Distinct from error red; no rebrand equivalent.
                warning: {
                    text: { value: '#8A6D0B' },
                    surface: { value: '#FBF3D9' },
                    border: { value: '#F0E2AE' },
                },
                // Hairline of a danger notice, beside `studio.danger.{bg,fg}`.
                danger: {
                    border: { value: '#F2CFC8' },
                },
                // The amber of the design system's glass accent: the expiry pill of a
                // proposal (text on the dark pill).
                accent: {
                    warning: { value: '#F5B84B' },
                },
                // The design system's secondary text (conditions paragraphs).
                inkSecondary: { value: '#4F4F4F' },
                // The veil behind a sheet or a modal of the creator space.
                overlayBg: { value: 'rgba(26, 26, 26, 0.42)' },
                // Flat sky blue of the creator-search moments of the freemium mock-ups:
                // the side panel of the onboarding sales step, and the colour block of
                // the Discovery upsells (searches used up, a prospect's identity locked).
                discoveryAccent: { value: '#7EC7FF' },
                // Warm pastel pink used as the base tint for the AI companion badge.
                // Distinct from lilac.200 (#E9E0F7) which is a cooler purple-blue.
                lilacBadge: { value: '#FAD5FF' },
                // Gradient overlay applied over the lilacBadge base in AiCompanionBadge.
                companionGradient: {
                    value: 'linear-gradient(135deg, rgba(255, 188, 126, 0.36) 0%, rgba(250, 213, 255, 0.95) 45%, rgba(255, 255, 255, 0.86) 100%)',
                },
                // Product announcement ("what's new") chrome. A self-contained dark
                // palette: the panel is a floating overlay and its indicator lives on
                // the dark top bar (`darkNav`), so neither inherits the cream ramp.
                // Every alpha is expressed against a dark backdrop on purpose — do not
                // reuse these on the cream surfaces.
                announcement: {
                    surface: { value: '#171715' },
                    surfaceBorder: { value: 'rgba(255, 255, 255, 0.10)' },
                    visualBg: { value: '#0F0F0E' },
                    visualBorder: { value: 'rgba(255, 255, 255, 0.08)' },
                    // Decorative lilac bloom bleeding out of the visual area's top-left.
                    visualHalo: {
                        value: 'radial-gradient(circle, rgba(185, 167, 255, 0.26), transparent 70%)',
                    },
                    closeBg: { value: 'rgba(0, 0, 0, 0.55)' },
                    closeBorder: { value: 'rgba(255, 255, 255, 0.20)' },
                    chipBg: { value: 'rgba(255, 255, 255, 0.06)' },
                    title: { value: '#FFFFFF' },
                    body: { value: 'rgba(255, 255, 255, 0.66)' },
                    counter: { value: 'rgba(255, 255, 255, 0.45)' },
                    // Signal green shared by the chip bullet and the top-bar unseen dot.
                    accent: { value: '#41D38D' },
                    controlBg: { value: 'rgba(255, 255, 255, 0.06)' },
                    controlFg: { value: 'rgba(255, 255, 255, 0.82)' },
                    controlHoverBg: { value: 'rgba(255, 255, 255, 0.12)' },
                    controlDisabled: { value: 'rgba(255, 255, 255, 0.24)' },
                    pagerIdle: { value: 'rgba(255, 255, 255, 0.26)' },
                    pagerActive: { value: '#FFFFFF' },
                    ctaBg: { value: '#FFFFFF' },
                    ctaFg: { value: '#171715' },
                    ctaHoverBg: { value: '#E8E4D9' },
                    // Top-bar trigger. It wears the panel's own chip — surface and hover
                    // come from `chipBg` and `controlHoverBg` — so only its text colours
                    // are its own.
                    indicatorFg: { value: '#FFFFFF' },
                    indicatorFgMuted: { value: 'rgba(255, 255, 255, 0.66)' },
                },
            },
        },
        semanticTokens: {
            colors: {
                // Warm the global hairline so inputs, selects, tables and dividers match
                // the cream/off-white surfaces instead of Chakra's cool default gray.
                border: {
                    DEFAULT: { value: '{colors.cardBorder}' },
                    emphasized: { value: '#DEDACE' },
                    // Warm subtle hairline for inner dividers (table rows, list separators)
                    // so they match the cream surfaces instead of Chakra's cool default.
                    subtle: { value: '#EFEDE7' },
                },
                primary: {
                    solid: { value: '{colors.primary.900}' },
                    contrast: { value: '{colors.softCream}' },
                    // fg is lighter than solid so the solid button hover is perceptible
                    // (charcoal-on-charcoal hover was previously a no-op).
                    fg: { value: '{colors.primary.700}' },
                    muted: { value: '{colors.primary.100}' },
                    subtle: { value: '{colors.primary.100}' },
                    emphasized: { value: '{colors.primary.200}' },
                    focusRing: { value: '{colors.primary.900}' },
                },
                lilac: {
                    solid: { value: '{colors.lilac.600}' },
                    contrast: { value: '{colors.white}' },
                    fg: { value: '{colors.lilac.700}' },
                    muted: { value: '{colors.lilac.100}' },
                    subtle: { value: '{colors.lilac.200}' },
                    emphasized: { value: '{colors.lilac.300}' },
                    focusRing: { value: '{colors.lilac.500}' },
                },
                rose: {
                    solid: { value: '{colors.rose.500}' },
                    contrast: { value: '{colors.white}' },
                    fg: { value: '{colors.rose.700}' },
                    muted: { value: '{colors.rose.100}' },
                    subtle: { value: '{colors.rose.200}' },
                    emphasized: { value: '{colors.rose.300}' },
                    focusRing: { value: '{colors.rose.500}' },
                },
                gray: {
                    subtle: { value: '{colors.gray.100}' },
                    fg: { value: '{colors.gray.700}' },
                    solid: { value: '{colors.gray.950}' },
                    focusRing: { value: '{colors.gray.800}' },
                },
                green: {
                    subtle: { value: '{colors.green.100}' },
                    fg: { value: '{colors.green.700}' },
                    solid: { value: '{colors.green.600}' },
                    focusRing: { value: '{colors.green.500}' },
                },
                deepForest: {
                    solid: { value: '{colors.deepForest}' },
                    contrast: { value: '{colors.white}' },
                    fg: { value: '{colors.deepForestHover}' },
                    muted: { value: 'rgba(0, 22, 18, 0.12)' },
                    subtle: { value: 'rgba(0, 22, 18, 0.08)' },
                    emphasized: { value: 'rgba(0, 22, 18, 0.16)' },
                    focusRing: { value: '{colors.deepForest}' },
                },
                // Brand Space Studio states. `override` marks a value that departs from
                // the shared page default (same language as the per-recipient blue in
                // collab briefs, hence the aliases — the hexes live in one place);
                // `available`/`danger`/`warning` back the publish, delete-variant and
                // program-destination surfaces.
                studio: {
                    override: {
                        bg: { value: '{colors.perRecipient.surfaceSubtle}' },
                        fg: { value: '{colors.perRecipient.text}' },
                        border: { value: '{colors.perRecipient.border}' },
                        surface: { value: '{colors.perRecipient.surface}' },
                    },
                    // Work the brand is only told about, never asked to fix — the blue
                    // reads as "in progress" where cream read as "nothing to see".
                    info: {
                        bg: { value: '{colors.perRecipient.surfaceSubtle}' },
                        fg: { value: '{colors.perRecipient.text}' },
                        border: { value: '{colors.perRecipient.borderSoft}' },
                    },
                    available: {
                        bg: { value: '#E8EEE0' },
                        fg: { value: '#2F6D4F' },
                    },
                    danger: {
                        bg: { value: '#FBEBE7' },
                        fg: { value: '#B4442C' },
                    },
                    warning: {
                        bg: { value: '#FFF7E8' },
                        border: { value: '#F0D49A' },
                        fg: { value: '#7A4B00' },
                    },
                },
                // Companion panel blocks (`@fluenceur/agent-blocks`). Every colour a
                // block paints comes from here, with a dark value, because the same
                // blocks are rendered by the dashboard and by the MCP widgets, which
                // follow the host's colour scheme. The base ink tokens (`ink`,
                // `inkMuted`, `softCream`...) have no dark value and cannot be given
                // one without a semantic token on their own path, which breaks
                // resolution (see the note below), hence a namespace of their own.
                //
                // The namespace is a Chakra colour palette: `colorPalette="agent"` on
                // a Button, IconButton, Checkbox or Badge paints it through the shared
                // recipes (solid, contrast, fg, subtle, muted, emphasized, focusRing),
                // so the blocks reuse the app's controls instead of restyling them.
                // `agent.warning` and `agent.danger` are palettes too (muted,
                // emphasized, fg) for the notices and the surface badge.
                agent: {
                    surface: { value: { base: '{colors.panelBg}', _dark: '#1C1B19' } },
                    // The quieter tint: metric tiles, table header, draft header.
                    muted: { value: { base: '{colors.background}', _dark: '#22211E' } },
                    // The fill of a control: hover of ghost buttons, user bubble, icon
                    // wells, chips, neutral notice.
                    subtle: { value: { base: '{colors.softCream}', _dark: '#2B2A26' } },
                    hoverBg: { value: { base: '{colors.rowHoverBg}', _dark: '#262522' } },
                    border: { value: { base: '{colors.cardBorder}', _dark: 'rgba(255, 255, 255, 0.12)' } },
                    emphasized: { value: { base: '{colors.border.emphasized}', _dark: 'rgba(255, 255, 255, 0.24)' } },
                    divider: { value: { base: '{colors.border.subtle}', _dark: 'rgba(255, 255, 255, 0.08)' } },
                    // Every word of the companion is one colour: `dark` in light mode,
                    // white in dark mode. There is no secondary or muted grey for text,
                    // on purpose: hierarchy is carried by size and weight alone (the
                    // `agent.*` text styles), never by colour. `fgBody` and `fgMuted`
                    // stay as names so the blocks keep saying what a line is, but they
                    // resolve to the same ink; do not give them a grey again.
                    fg: { value: { base: '{colors.dark}', _dark: '{colors.white}' } },
                    fgBody: { value: { base: '{colors.dark}', _dark: '{colors.white}' } },
                    fgMuted: { value: { base: '{colors.dark}', _dark: '{colors.white}' } },
                    // The one exception to the no-grey rule: the placeholder of an input,
                    // so an empty field never reads as a filled one. Placeholders only,
                    // never text.
                    placeholder: { value: { base: '{colors.inkMuted}', _dark: '#ABA597' } },
                    // Decorative icons and list markers only, never text: below AA on purpose.
                    fgDecorative: { value: { base: '{colors.inkSubtle}', _dark: '#7F796E' } },
                    solid: { value: { base: '{colors.darkNav}', _dark: '#F4F3EC' } },
                    solidHover: { value: { base: '{colors.primary.700}', _dark: '#FFFFFF' } },
                    contrast: { value: { base: '{colors.white}', _dark: '{colors.ink}' } },
                    // Same charcoal ring as the dashboard (`primary.focusRing`), light
                    // on the dark surface.
                    focusRing: { value: { base: '{colors.primary.900}', _dark: '#F4F3EC' } },
                    // Lilac, the AI signature: the trace's sparks, shimmer and pulse.
                    accent: { value: { base: '{colors.lilac.600}', _dark: '{colors.lilac.400}' } },
                    // Initials of a creator avatar: the dashboard's `brandAvatarTint`.
                    avatar: {
                        bg: { value: { base: '{colors.lilac.200}', _dark: 'rgba(185, 167, 255, 0.16)' } },
                        fg: { value: { base: '{colors.lilac.700}', _dark: '#CDBEF5' } },
                    },
                    success: {
                        muted: { value: { base: '{colors.green.100}', _dark: 'rgba(37, 164, 129, 0.18)' } },
                        // green.700, not green.600: #178468 is 4.4:1 on the cream tiles.
                        fg: { value: { base: '{colors.green.700}', _dark: '#6FD6B0' } },
                        icon: { value: { base: '{colors.green.600}', _dark: '#5FD0A8' } },
                    },
                    // `warning.text` (#8A6D0B) is 4.4:1 on its own surface; darkened
                    // here so the notice text passes AA.
                    warning: {
                        muted: { value: { base: '{colors.warning.surface}', _dark: 'rgba(240, 206, 106, 0.10)' } },
                        emphasized: { value: { base: '{colors.warning.border}', _dark: 'rgba(240, 206, 106, 0.28)' } },
                        fg: { value: { base: '#765C08', _dark: '#F0CE6A' } },
                        focusRing: { value: { base: '#765C08', _dark: '#F0CE6A' } },
                    },
                    danger: {
                        muted: { value: { base: '{colors.studio.danger.bg}', _dark: 'rgba(242, 160, 142, 0.12)' } },
                        emphasized: { value: { base: '{colors.danger.border}', _dark: 'rgba(242, 160, 142, 0.28)' } },
                        fg: { value: { base: '#A63D27', _dark: '#F2A08E' } },
                    },
                    // Match flames. Rank is told by the number of lit flames, never by
                    // the hue, so the lit pair is the same in both schemes.
                    flame: {
                        litOuter: { value: '#FF6723' },
                        litInner: { value: '#FFB02E' },
                        unlitOuter: { value: { base: '#E4DFD3', _dark: '#3D3A34' } },
                        unlitInner: { value: { base: '#EFEBE1', _dark: '#4A463F' } },
                    },
                },
                // NOTE: perRecipient.* and warning.* exist as base tokens only (defined
                // in tokens.colors above, like activeSidebarLink.*). Do not alias them
                // here: a semantic token on the same path overrides the base token's
                // CSS variable with a self-reference, which breaks resolution — the
                // per-recipient blue borders rendered as neutral because of it.
            },
        },
        layerStyles: {
            // The frame of a companion block (row lists, tables, drafts, files):
            // the dashboard card (`cardBg` + `border`, `lg`, no shadow) painted
            // from `agent.*` so it follows the host's colour scheme.
            'agent.card': {
                value: {
                    bg: 'agent.surface',
                    borderWidth: '1px',
                    borderColor: 'agent.border',
                    borderRadius: 'lg',
                },
            },
        },
        // Creator space typography (additive, read only by the portal): the
        // mock-ups' titles, section labels, list rows and amounts.
        textStyles: {
            'creator.pageTitle': {
                value: { fontFamily: 'heading', fontWeight: '700', fontSize: '28px', letterSpacing: '-0.025em', lineHeight: '1.12' },
            },
            'creator.detailTitle': {
                value: { fontFamily: 'heading', fontWeight: '700', fontSize: '17px', letterSpacing: '-0.02em', lineHeight: '1.2' },
            },
            'creator.sheetTitle': {
                value: { fontFamily: 'heading', fontWeight: '700', fontSize: '22px', letterSpacing: '-0.02em', lineHeight: '1.15' },
            },
            'creator.sectionLabel': {
                value: {
                    fontFamily: 'heading',
                    fontWeight: '600',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'inkMuted',
                },
            },
            'creator.rowTitle': {
                value: { fontWeight: '500', fontSize: '14.5px', lineHeight: '1.35', color: 'primary.900' },
            },
            'creator.rowMeta': {
                value: { fontSize: '12.5px', lineHeight: '1.45', color: 'inkMuted' },
            },
            'creator.amountStrip': {
                value: { fontFamily: 'heading', fontWeight: '600', fontSize: '15px', lineHeight: '1.3' },
            },
            'creator.amountHero': {
                value: { fontFamily: 'heading', fontWeight: '700', fontSize: '44px', letterSpacing: '-0.03em', lineHeight: '1' },
            },
            'creator.amountOffer': {
                value: { fontFamily: 'heading', fontWeight: '700', fontSize: '36px', letterSpacing: '-0.03em', lineHeight: '1' },
            },
            // Companion panel typography (`@fluenceur/agent-blocks`): the 440px
            // panel sits one notch denser than the dashboard, so its half steps
            // live here once rather than in every block. No colour: the blocks
            // paint from `agent.*`.
            // A row's or a card's title: an action to handle, a file, a creator.
            'agent.title': {
                value: { fontFamily: 'heading', fontWeight: '600', fontSize: '13.5px', lineHeight: '1.35' },
            },
            // Sentences inside a block: status lines, notices, table cells.
            'agent.text': {
                value: { fontSize: '13px', lineHeight: '1.5' },
            },
            // Small words in the heading face: chips, link actions, toggles.
            'agent.label': {
                value: { fontFamily: 'heading', fontWeight: '500', fontSize: '12.5px', lineHeight: '1.3' },
            },
            // The line under a title: meta, urgency, trace steps, captions.
            'agent.meta': {
                value: { fontSize: 'xs', lineHeight: '1.45' },
            },
            // The label above a figure: metric tiles, initials.
            'agent.overline': {
                value: { fontFamily: 'heading', fontWeight: '600', fontSize: '11px', lineHeight: '1.3' },
            },
        },
        keyframes: {
            // Countdown ring on toasts: empties a circle of circumference 81.7
            // (r=13, see the toaster's ToastCountdownRing) over the toast duration.
            'toast-ring': {
                from: { strokeDashoffset: '0' },
                to: { strokeDashoffset: '81.7' },
            },
            'toast-blur-in': {
                '0%': { filter: 'blur(4px)' },
                '60%, 100%': { filter: 'blur(0px)' },
            },
            'toast-slide-out': {
                from: { transform: 'translateX(0)' },
                to: { transform: 'translateX(34px)' },
            },
            // Loader mark (LoaderMark / LoadingPage). The Fluenceur glyph is cut into
            // three horizontal bands; each one draws itself in left to right, holds,
            // then wipes out. The `-ink` pair carries the accent-to-ink hand-off and
            // reads --loader-accent / --loader-ink, which the component sets.
            'loader-draw': {
                '0%': { clipPath: 'inset(0 100% 0 0)' },
                '46%, 80%': { clipPath: 'inset(0 0 0 0)' },
                '100%': { clipPath: 'inset(0 0 0 100%)' },
            },
            'loader-draw-ink': {
                '0%, 34%': { color: 'var(--loader-accent)' },
                '54%, 100%': { color: 'var(--loader-ink)' },
            },
            // Accent highlight riding just ahead of the band as it draws.
            'loader-glint': {
                '0%, 40%': { clipPath: 'inset(0 100% 0 0)', opacity: '0' },
                '46%': { clipPath: 'inset(0 84% 0 4%)', opacity: '0.95' },
                '62%': { clipPath: 'inset(0 0 0 90%)', opacity: '0' },
                '100%': { clipPath: 'inset(0 0 0 100%)', opacity: '0' },
            },
            // Companion reasoning trace: a light band sweeping across the running
            // label (a text gradient, never a surface), and the pulsing dot of the
            // active step. Both are switched off under prefers-reduced-motion by
            // the blocks that use them.
            'agent-shimmer': {
                from: { backgroundPosition: '150% 0' },
                to: { backgroundPosition: '-50% 0' },
            },
            'agent-pulse-dot': {
                '0%, 100%': { opacity: '0.35', transform: 'scale(0.8)' },
                '50%': { opacity: '1', transform: 'scale(1)' },
            },
            // Barely-there breath once the three bands have landed.
            'loader-settle': {
                '0%, 58%': { transform: 'scale(1)' },
                '68%': { transform: 'scale(1.022)' },
                '80%, 100%': { transform: 'scale(1)' },
            },
        },
        recipes: {
            badge: badgeRecipe,
            button: buttonRecipe,
            heading: headingRecipe,
            input: inputRecipe,
            textarea: textareaRecipe,
        },
        slotRecipes: {
            checkbox: checkboxRecipe,
            alert: alertRecipe,
            barSegment: barSegmentRecipe,
            switch: switchRecipe,
            dialog: dialogRecipe,
            drawer: drawerRecipe,
            select: selectRecipe,
            nativeSelect: nativeSelectRecipe,
            numberInput: numberInputRecipe,
            radioCard: radioCardRecipe,
            datePicker: datePickerRecipe,
            combobox: comboboxRecipe,
            tagsInput: tagsInputRecipe,
            menu: menuRecipe,
            table: tableRecipe,
            card: cardRecipe,
            tabs: tabsRecipe,
            field: fieldRecipe,
            actionBar: actionBarRecipe,
            toast: toastRecipe,
            tooltip: tooltipRecipe,
            popover: popoverRecipe,
        },
    },
});
export const system = createSystem(defaultConfig, config);
